import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal } from "../components/Reveal";
import { services, features, company } from "../mock";

function PageHero({ eyebrow, title, sub, image }) {
  return (
    <section className="relative pt-44 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">{eyebrow}</span>
        <h1 className="mt-3 font-display font-black text-white text-4xl md:text-6xl tracking-tight max-w-3xl">{title}</h1>
        <p className="mt-4 text-zinc-300 text-lg max-w-2xl">{sub}</p>
      </div>
    </section>
  );
}

function ServiceRow({ service, index }) {
  return (
    <Reveal>
      <div id={service.id} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div className="relative rounded-xl overflow-hidden group">
          <img src={service.image} alt={service.title} className="w-full h-[360px] object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-xl" />
        </div>
        <div>
          <span className="text-orange-500 font-semibold text-sm tracking-[0.15em] uppercase">
            {String(index + 1).padStart(2, "0")} — Service
          </span>
          <h2 className="mt-2 font-display font-extrabold text-white text-3xl md:text-4xl tracking-tight">{service.title}</h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">{service.body}</p>
          <Link to="/contact">
            <Button className="mt-7 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
              Request This Service <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

function FeatureCard({ feature, delay }) {
  return (
    <Reveal delay={delay}>
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-7 h-full">
        <span className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center">
          <Check className="w-5 h-5 text-orange-400" />
        </span>
        <h4 className="font-display font-bold text-white text-lg mt-4">{feature.title}</h4>
        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{feature.text}</p>
      </div>
    </Reveal>
  );
}

function WhyChooseUs() {
  return (
    <section className="py-20 bg-zinc-900/50">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <h2 className="font-display font-extrabold text-white text-3xl md:text-4xl text-center">Why Choose Phoenix</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {features.map((f, i) => (
            <FeatureCard key={f.title} feature={f} delay={i * 90} />
          ))}
        </div>
        <div className="text-center mt-12">
          <a href={`tel:${company.phone}`} className="text-zinc-400">Have a project in mind? Call <span className="text-orange-400 font-semibold">{company.phone}</span></a>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title="Industrial & Commercial Floor Coating Systems"
        sub="From heavy-duty resinous and ESD flooring to polished concrete, restorations and traffic-deck coatings — every system is engineered for the demands of large-scale facilities."
        image="/images/industrial.jpg"
      />

      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 space-y-24">
          {services.map((s, i) => (
            <ServiceRow key={s.id} service={s} index={i} />
          ))}
        </div>
      </section>

      <WhyChooseUs />
    </>
  );
}
